import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  PricebookAssignmentEditorWizard,
  toast,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  VerticalStack,
  type AssignmentEditorFormValue,
  type AssignmentEditorViewModel,
  type CommerceSelectOptionViewModel,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';

import { useNavigation } from '@/context/NavigationContext';
import { useSession } from '@/context/SessionContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const ASSIGNMENT_SORT_FIELDS = ['created_at', 'status', 'subject', 'target', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  CommerceBreadcrumbHeader,
  CommerceRegistryUtilityBand,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  metadataEntriesToRecord,
  metadataRecordToEntries,
} from '@/views/commerce/catalogHierarchyShared';
import {
  createAssignment,
  createCommissionPlanAssignment,
  deleteAssignment,
  deleteCommissionPlanAssignment,
  getAssignment,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getCommissionPlanAssignment,
  listAssignments,
  listCommissionPlanAssignments,
  listCommissionPlans,
  listPricebooks,
  listPublications,
  updateAssignment,
  updateCommissionPlanAssignment,
  type CatalogPublication,
  type CommissionPlan,
  type CommissionPlanAssignment,
  type Pricebook,
  type PricebookAssignment,
} from '@/lib/commerce';
import {
  buildObservedScopeOptions,
  buildPublicationSelectorOptions,
  buildTenantSelectorOptions,
} from '@/views/commerce/selectorOptions';

type ValidationErrors = Record<string, string>;
type AssignmentTarget = AssignmentEditorFormValue['target'];
type AssignmentTargetKind = AssignmentTarget['kind'];
type AssignmentMode = 'pricebook' | 'commission';

type AssignmentRecord = PricebookAssignment | CommissionPlanAssignment;

interface AssignmentModeConfig {
  subjectLabel: string;
  subjectPluralLabel: string;
  modeButtonLabel: string;
  emptyTitle: string;
  emptyBody: string;
  createLabel: string;
  createSuccess: string;
  updateSuccess: string;
  saveError: string;
  loadingError: string;
  editorSubtitle: string;
}

const assignmentModeConfig: Record<AssignmentMode, AssignmentModeConfig> = {
  pricebook: {
    subjectLabel: 'Pricebook',
    subjectPluralLabel: 'Pricebook Assignments',
    modeButtonLabel: 'Pricebooks',
    emptyTitle: 'No Pricebook Assignments',
    emptyBody: 'Assign pricebooks to publications, organizations, tenants, stores, or agents to define targeted pricing.',
    createLabel: 'New Pricebook Assignment',
    createSuccess: 'Pricebook assignment created',
    updateSuccess: 'Pricebook assignment updated',
    saveError: 'Failed to save pricebook assignment.',
    loadingError: 'Failed to load pricebook assignment detail.',
    editorSubtitle: 'Route one pricebook to one publication, organization, tenant, store, or agent scope.',
  },
  commission: {
    subjectLabel: 'Commission Plan',
    subjectPluralLabel: 'Commission Plan Assignments',
    modeButtonLabel: 'Commission Plans',
    emptyTitle: 'No Commission Plan Assignments',
    emptyBody: 'Assign commission plans to publications, organizations, tenants, stores, or agents to define payout overrides.',
    createLabel: 'New Commission Assignment',
    createSuccess: 'Commission assignment created',
    updateSuccess: 'Commission assignment updated',
    saveError: 'Failed to save commission assignment.',
    loadingError: 'Failed to load commission assignment detail.',
    editorSubtitle: 'Route one commission plan to one publication, organization, tenant, store, or agent scope.',
  },
};

function emptyAssignmentForm(): AssignmentEditorFormValue {
  return {
    pricebookId: '',
    target: { kind: 'publication' },
    status: 'active',
    startsAt: '',
    endsAt: '',
    metadata: [],
  };
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toISOOrUndefined(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

function normalizeTarget(target: AssignmentTarget): AssignmentTarget {
  switch (target.kind) {
    case 'publication':
      return {
        kind: 'publication',
        publicationId: target.publicationId || '',
      };
    case 'organization':
      return {
        kind: 'organization',
        organizationId: target.organizationId || '',
      };
    case 'tenant':
      return {
        kind: 'tenant',
        sellingTenantId: target.sellingTenantId || '',
      };
    case 'store':
      return {
        kind: 'store',
        storeId: target.storeId || '',
      };
    case 'agent':
      return {
        kind: 'agent',
        agentUserId: target.agentUserId || '',
      };
    default:
      return { kind: 'publication' };
  }
}

function inferTargetKind(record: AssignmentRecord): AssignmentTargetKind {
  return (record.target_kind as AssignmentTargetKind) || (
    record.publication_id ? 'publication' :
      record.organization_id ? 'organization' :
        record.selling_tenant_id ? 'tenant' :
          record.store_id ? 'store' :
            'agent'
  );
}

function assignmentToForm(record: AssignmentRecord, mode: AssignmentMode): AssignmentEditorFormValue {
  const subjectId = mode === 'pricebook'
    ? ('pricebook_id' in record ? record.pricebook_id : '')
    : ('commission_plan_id' in record ? record.commission_plan_id : '');
  return {
    pricebookId: subjectId,
    target: normalizeTarget({
      kind: inferTargetKind(record),
      publicationId: record.publication_id || '',
      organizationId: record.organization_id || '',
      sellingTenantId: record.selling_tenant_id || '',
      storeId: record.store_id || '',
      agentUserId: record.agent_user_id || '',
    }),
    status: record.status,
    startsAt: toDatetimeLocal(record.starts_at),
    endsAt: toDatetimeLocal(record.ends_at),
    metadata: metadataRecordToEntries(record.metadata),
  };
}

function validateAssignmentForm(form: AssignmentEditorFormValue, subjectLabel: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!form.pricebookId.trim()) {
    errors.pricebookId = `Choose a ${subjectLabel.toLowerCase()}.`;
  }

  switch (form.target.kind) {
    case 'publication':
      if (!form.target.publicationId?.trim()) {
        errors['target.publicationId'] = 'Choose a publication.';
      }
      break;
    case 'organization':
      if (!form.target.organizationId?.trim()) {
        errors['target.organizationId'] = 'Enter an organization id.';
      }
      break;
    case 'tenant':
      if (!form.target.sellingTenantId?.trim()) {
        errors['target.sellingTenantId'] = 'Enter a selling tenant id.';
      }
      break;
    case 'store':
      if (!form.target.storeId?.trim()) {
        errors['target.storeId'] = 'Enter a store id.';
      }
      break;
    case 'agent':
      if (!form.target.agentUserId?.trim()) {
        errors['target.agentUserId'] = 'Enter an agent user id.';
      }
      break;
  }

  return errors;
}

function buildAssignmentPayload(
  form: AssignmentEditorFormValue,
  mode: AssignmentMode,
  catalogOwnerTenantId?: string | null,
): Record<string, unknown> {
  const target = normalizeTarget(form.target);
  const payload: Record<string, unknown> = {
    publication_id: target.kind === 'publication' ? target.publicationId?.trim() || undefined : undefined,
    organization_id: target.kind === 'organization' ? target.organizationId?.trim() || undefined : undefined,
    selling_tenant_id: target.kind === 'tenant' ? target.sellingTenantId?.trim() || undefined : undefined,
    store_id: target.kind === 'store' ? target.storeId?.trim() || undefined : undefined,
    agent_user_id: target.kind === 'agent' ? target.agentUserId?.trim() || undefined : undefined,
    status: form.status,
    starts_at: toISOOrUndefined(form.startsAt),
    ends_at: toISOOrUndefined(form.endsAt),
    metadata: metadataEntriesToRecord(form.metadata),
  };

  if (mode === 'pricebook') {
    payload.pricebook_id = form.pricebookId;
  } else {
    payload.commission_plan_id = form.pricebookId;
  }

  if (catalogOwnerTenantId?.trim()) {
    payload.catalog_owner_tenant_id = catalogOwnerTenantId.trim();
  }

  return payload;
}

function assignmentSubjectName(
  record: AssignmentRecord,
  mode: AssignmentMode,
  pricebooks: Pricebook[],
  commissionPlans: CommissionPlan[],
): string {
  if (mode === 'pricebook') {
    const pricebookID = 'pricebook_id' in record ? record.pricebook_id : '';
    return (
      pricebooks.find(pricebook => pricebook.pricebook_id === pricebookID)?.name ||
      ('pricebook_name' in record ? record.pricebook_name : null) ||
      pricebookID
    );
  }
  const commissionPlanID = 'commission_plan_id' in record ? record.commission_plan_id : '';
  return (
    commissionPlans.find(plan => plan.commission_plan_id === commissionPlanID)?.name ||
    ('commission_plan_name' in record ? record.commission_plan_name : null) ||
    commissionPlanID
  );
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'inactive':
    case 'disabled':
      return 'muted';
    default:
      return 'default';
  }
}

export default function AssignmentsPage() {
  const { activeTenant, organizations } = useScope();
  const { session } = useSession();
  const navigation = useNavigation();
  const sessionScopes = useMemo(() => new Set(session?.scopes ?? []), [session?.scopes]);
  const canManagePricebookAssignments = sessionScopes.has('tenant.pricebooks.manage');
  const canManageCommissionAssignments = sessionScopes.has('tenant.commissions.manage');
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active', 'disabled', 'inactive'] },
    sortFields: ASSIGNMENT_SORT_FIELDS,
    registryValues: ['pricebook', 'commission'],
  });
  const assignmentMode: AssignmentMode = query.registry === 'commission'
    ? 'commission'
    : query.registry === 'pricebook'
      ? 'pricebook'
      : canManagePricebookAssignments ? 'pricebook' : 'commission';
  const [pricebookAssignments, setPricebookAssignments] = useState<PricebookAssignment[]>([]);
  const [commissionAssignments, setCommissionAssignments] = useState<CommissionPlanAssignment[]>([]);
  const [pricebooks, setPricebooks] = useState<Pricebook[]>([]);
  const [commissionPlans, setCommissionPlans] = useState<CommissionPlan[]>([]);
  const [publications, setPublications] = useState<CatalogPublication[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [form, setForm] = useState<AssignmentEditorFormValue>(emptyAssignmentForm());

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);

  const hasAssignmentAccess = canManagePricebookAssignments || canManageCommissionAssignments;
  const activeModeEnabled = assignmentMode === 'pricebook' ? canManagePricebookAssignments : canManageCommissionAssignments;
  const modeConfig = assignmentModeConfig[assignmentMode];

  useEffect(() => {
    if (assignmentMode === 'pricebook' && !canManagePricebookAssignments && canManageCommissionAssignments) {
      query.setRegistry('commission');
      return;
    }
    if (assignmentMode === 'commission' && !canManageCommissionAssignments && canManagePricebookAssignments) {
      query.setRegistry('pricebook');
    }
  }, [assignmentMode, canManageCommissionAssignments, canManagePricebookAssignments, query.setRegistry]);

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorLoading(false);
    setEditingId(null);
    setValidationErrors({});
  };

  const handleDelete = useCallback(async (record: AssignmentRecord) => {
    const subjectName = assignmentSubjectName(record, assignmentMode, pricebooks, commissionPlans);
    if (!window.confirm(`Archive assignment for "${subjectName}"?`)) {
      return;
    }

    setError(null);
    try {
      if (assignmentMode === 'pricebook') {
        await deleteAssignment(record.assignment_id);
      } else {
        await deleteCommissionPlanAssignment(record.assignment_id);
      }
      await loadData();
      toast.success(`${modeConfig.subjectLabel} assignment archived`, {
        description: subjectName,
      });
      if (editingId === record.assignment_id) {
        closeEditor();
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const message = getCommerceErrorMessage(err, `Failed to archive ${modeConfig.subjectLabel.toLowerCase()} assignment.`);
        setError(message);
        toast.error(`Failed to archive ${modeConfig.subjectLabel.toLowerCase()} assignment`, { description: message });
      }
    }
  }, [assignmentMode, closeEditor, commissionPlans, editingId, modeConfig.subjectLabel, pricebooks]);

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id || !hasAssignmentAccess) {
      setPricebookAssignments([]);
      setCommissionAssignments([]);
      setPricebooks([]);
      setCommissionPlans([]);
      setPublications([]);
      setTotalAssignments(0);
      setStatusCounts({});
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [nextPublications, nextPricebooks, nextCommissionPlans, nextAssignments] = await Promise.all([
        listPublications({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        canManagePricebookAssignments
          ? listPricebooks({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items)
          : Promise.resolve([] as Pricebook[]),
        canManageCommissionAssignments
          ? listCommissionPlans({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items)
          : Promise.resolve([] as CommissionPlan[]),
        assignmentMode === 'pricebook'
          ? listAssignments({
              catalogOwnerTenantId: activeTenant.id,
              search: searchQuery || undefined,
              status: query.facets.status,
              sort: query.sort,
              include_facets: ['status'],
              ...toLimitOffset(query.pagination),
            })
          : listCommissionPlanAssignments({
              catalogOwnerTenantId: activeTenant.id,
              search: searchQuery || undefined,
              status: query.facets.status,
              sort: query.sort,
              include_facets: ['status'],
              ...toLimitOffset(query.pagination),
            }),
      ]);
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextAssignments.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setPublications(nextPublications);
      setPricebooks(nextPricebooks);
      setCommissionPlans(nextCommissionPlans);
      if (assignmentMode === 'pricebook') {
        setPricebookAssignments(nextAssignments.items as PricebookAssignment[]);
        setCommissionAssignments([]);
      } else {
        setCommissionAssignments(nextAssignments.items as CommissionPlanAssignment[]);
        setPricebookAssignments([]);
      }
      setTotalAssignments(nextAssignments.total_count);
      setStatusCounts(Object.fromEntries((nextAssignments.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
    } catch (err) {
      if (requestId === requestSequence.current) setError(getCommerceErrorMessage(err, 'Failed to load assignments.'));
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [activeTenant?.id, assignmentMode, canManageCommissionAssignments, canManagePricebookAssignments, hasAssignmentAccess, query.facets.status, query.pagination, query.sort, searchQuery]);

  const publicationOptions = useMemo<CommerceSelectOptionViewModel[]>(
    () => buildPublicationSelectorOptions(publications),
    [publications],
  );
  const organizationOptions = useMemo(
    () => buildObservedScopeOptions(publications, 'organizations'),
    [publications],
  );
  const tenantOptions = useMemo(
    () =>
      buildTenantSelectorOptions({
        activeTenant,
        organizations,
        observedTenantIds: publications.flatMap(publication => publication.selling_tenant_ids || []),
      }),
    [activeTenant, organizations, publications],
  );
  const storeOptions = useMemo(() => buildObservedScopeOptions(publications, 'stores'), [publications]);
  const agentOptions = useMemo(() => buildObservedScopeOptions(publications, 'agents'), [publications]);

  const activeAssignments = assignmentMode === 'pricebook' ? pricebookAssignments : commissionAssignments;

  const openCreateEditor = () => {
    setEditingId(null);
    setValidationErrors({});
    setForm(emptyAssignmentForm());
    setEditorOpen(true);
  };

  const openEditEditor = useCallback(async (assignmentId: string) => {
    setEditingId(assignmentId);
    setValidationErrors({});
    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const detail = assignmentMode === 'pricebook'
        ? await getAssignment(assignmentId)
        : await getCommissionPlanAssignment(assignmentId);
      setForm(assignmentToForm(detail, assignmentMode));
    } catch (err) {
      closeEditor();
      setError(getCommerceErrorMessage(err, modeConfig.loadingError));
    } finally {
      setEditorLoading(false);
    }
  }, [assignmentMode, modeConfig.loadingError]);

  const columns = useMemo<ColumnDef<AssignmentRecord>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'subject',
      accessorFn: record => assignmentSubjectName(record, assignmentMode, pricebooks, commissionPlans),
      header: modeConfig.subjectLabel,
      size: 250,
      cell: ({ row }) => {
        const record = row.original;
        const name = assignmentSubjectName(record, assignmentMode, pricebooks, commissionPlans);
        return (
          <button
            type="button"
            onClick={() => void openEditEditor(record.assignment_id)}
            className="group min-w-0 text-left"
            aria-label={`Edit assignment for ${name}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {record.status} • {record.validity_label || 'Always active'}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'target',
      accessorFn: record => record.target_label || record.target_summary || '',
      header: 'Target',
      size: 250,
      cell: ({ row }) => {
        const record = row.original;
        const publicationName = publications.find(p => p.publication_id === record.publication_id)?.storefront_key || record.publication_label || null;
        return (
          <div className="min-w-0">
            <p className="truncate text-[11px] font-black uppercase tracking-tight text-foreground/80">
              {record.target_label || record.target_summary || 'Direct assignment'}
            </p>
            {publicationName && (
              <DescriptionText className="truncate text-[10px]">
                via {publicationName}
              </DescriptionText>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 120,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const record = row.original;
        const name = assignmentSubjectName(record, assignmentMode, pricebooks, commissionPlans);
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => void openEditEditor(record.assignment_id)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
              aria-label={`Modify assignment for ${name}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void handleDelete(record)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-rose-400/50 hover:text-rose-600 hover:bg-rose-500/5"
              aria-label={`Archive assignment for ${name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [assignmentMode, commissionPlans, handleDelete, openEditEditor, pricebooks, publications, modeConfig.subjectLabel]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: ASSIGNMENT_SORT_FIELDS, registry: 'assignments' });

  const handleSave = async () => {
    const nextErrors = validateAssignmentForm(form, modeConfig.subjectLabel);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setError('Resolve the highlighted assignment validation errors before saving.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildAssignmentPayload(form, assignmentMode, editingId ? null : activeTenant?.id);
      if (editingId) {
        if (assignmentMode === 'pricebook') {
          await updateAssignment(editingId, payload);
        } else {
          await updateCommissionPlanAssignment(editingId, payload);
        }
        toast.success(modeConfig.updateSuccess);
      } else {
        if (assignmentMode === 'pricebook') {
          await createAssignment(payload);
        } else {
          await createCommissionPlanAssignment(payload);
        }
        toast.success(modeConfig.createSuccess);
      }
      await loadData();
      closeEditor();
    } catch (err) {
      const msg = getCommerceErrorMessage(err, modeConfig.saveError);
      setError(msg);
      toast.error(modeConfig.saveError, { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (mode: AssignmentMode) => {
    if ((mode === 'pricebook' && !canManagePricebookAssignments) || (mode === 'commission' && !canManageCommissionAssignments)) {
      return;
    }
    closeEditor();
    query.setRegistry(mode);
    setError(null);
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus: hasAssignmentAccess ? accessDeniedStatus : 403,
    activeSectionId: 'assignments',
    searchQuery,
    searchPlaceholder: `Search ${modeConfig.subjectPluralLabel.toLowerCase()}...`,
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage pricebook or commission assignments.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'create-assignment',
      label: modeConfig.createLabel,
      onAction: openCreateEditor,
      disabled: !activeModeEnabled,
    },
  });

  const editorViewModel: AssignmentEditorViewModel = {
    mode: editingId ? 'edit' : 'create',
    state: saving ? 'saving' : editorLoading ? 'loading' : 'ready',
    isOpen: editorOpen,
    title: editingId ? `Edit ${modeConfig.subjectLabel} Assignment` : modeConfig.createLabel,
    subtitle: modeConfig.editorSubtitle,
    saveActionLabel: editingId ? 'Update Assignment' : 'Create Assignment',
    subjectLabel: modeConfig.subjectLabel,
    assignmentId: editingId,
    value: form,
    pricebookOptions: assignmentMode === 'pricebook'
      ? pricebooks.map(pricebook => ({
          value: pricebook.pricebook_id,
          label: pricebook.name,
          description: pricebook.selling_tenant_id || activeTenant?.name || undefined,
          helperText: pricebook.coverage?.variant_coverage_label || undefined,
        }))
      : commissionPlans.map(plan => ({
          value: plan.commission_plan_id,
          label: plan.name,
          description: plan.selling_tenant_id || activeTenant?.name || undefined,
          helperText: plan.currency || undefined,
        })),
    publicationOptions,
    organizationOptions,
    tenantOptions,
    storeOptions,
    agentOptions,
    statusOptions: [
      { value: 'active', label: 'Active' },
      { value: 'disabled', label: 'Disabled' },
    ],
    targetOptions: [
      { value: 'publication', label: 'Publication' },
      { value: 'organization', label: 'Organization' },
      { value: 'tenant', label: 'Tenant' },
      { value: 'store', label: 'Store' },
      { value: 'agent', label: 'Agent' },
    ],
    targetRules: [
      {
        id: 'single-scope',
        label: 'Single scope strategy',
        body: 'Each assignment may target only one publication, organization, tenant, store, or agent scope at a time.',
      },
      {
        id: 'publication-first',
        label: 'Publication-scoped curation',
        body: 'Assignment targeting uses searchable selectors so operators can choose one normalized publication, organization, tenant, store, or agent scope without hand-entering ids.',
      },
    ],
    fieldHelp: [
      {
        field: 'startsAt',
        label: 'Validity window',
        body: 'Leave starts and ends blank for always-on assignments.',
        tone: 'info',
      },
    ],
    validationErrors,
    onChange: patch => {
      setValidationErrors({});
      setForm(current => ({ ...current, ...patch }));
    },
    onChangeTarget: target => {
      setValidationErrors({});
      setForm(current => ({ ...current, target: normalizeTarget(target) }));
    },
    onSave: handleSave,
    onClose: closeEditor,
  };

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Assignments' }]}
          title="Assignments"
          description="Direct targeted pricebooks and commission plans to publications, reseller tenants, organizations, stores, and agents."
        />
      }
      takeoverActive={editorOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={<PricebookAssignmentEditorWizard viewModel={editorViewModel} />}
    >
      <RegistryWorkspace>
        <CommerceRegistryUtilityBand className="shrink-0 flex flex-wrap gap-2">
          {(['pricebook', 'commission'] as AssignmentMode[]).map(mode => {
            const enabled = mode === 'pricebook' ? canManagePricebookAssignments : canManageCommissionAssignments;
            const selected = assignmentMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => handleModeChange(mode)}
                disabled={!enabled}
                className={
                  selected
                    ? 'rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-[10px] font-black uppercase tracking-tight text-foreground'
                    : 'rounded-md border border-border/50 bg-background px-3 py-2 text-[10px] font-black uppercase tracking-tight text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50'
                }
              >
                {assignmentModeConfig[mode].modeButtonLabel}
              </button>
            );
          })}
        </CommerceRegistryUtilityBand>

        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={activeAssignments}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={columnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const filter = next.find(item => item.id === 'status');
              query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalAssignments}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">{modeConfig.emptyTitle}</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.status.length
                    ? `No ${modeConfig.subjectPluralLabel.toLowerCase()} matched the current search or filters.`
                    : modeConfig.emptyBody}
                </DescriptionText>
                {searchQuery || query.facets.status.length ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search and filters
                  </button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={query.setSearch}
                searchPlaceholder={`Search all ${modeConfig.subjectPluralLabel.toLowerCase()}...`}
                resultCount={totalAssignments}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <DataTableFacetedFilter
                    title="Status"
                    selectedValues={new Set(query.facets.status)}
                    onFilterChange={values => query.setFacet('status', Array.from(values))}
                    counts={statusCounts}
                    options={['active', 'disabled', 'inactive'].map(value => ({
                      label: value.charAt(0).toUpperCase() + value.slice(1),
                      value,
                    }))}
                  />
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
